import dayjs from 'dayjs';
import { produce } from 'immer';

import { getTimeRangeBasedOnRetention, validateFilterDate, validateFilterDateRange } from 'modules/time';

import { Activity, DetectionTypes } from 'types/aspen/activity';
import { ActivityState } from 'types/aspen/events';
import { QueryStatesPayload } from 'types/aspen/socketMessages';
import { LocalFilterState } from 'types/events';

const AI_EVENTS: Array<DetectionTypes> = ['person', 'package', 'vehicle', 'pet'];
const MOTION_EVENTS: Array<DetectionTypes> = ['object', 'motion'];
const SOUND_EVENTS: Array<DetectionTypes> = ['sound', 'sound_smoke_alarm', 'sound_co_alarm'];

export const groupEventsByDate = (events: Array<ActivityState>): Map<string, Array<ActivityState>> =>
  events.reduce(
    (groupedEvents, event) =>
      groupedEvents.set(dayjs(event.timestamp).format('YYYY-MM-DD'), [
        ...(groupedEvents.get(dayjs(event.timestamp).format('YYYY-MM-DD')) || []),
        event,
      ]),
    new Map<string, Array<ActivityState>>(),
  );

interface MergeEventsParams {
  currentEvents: Array<ActivityState>;
  nextEvents: Array<ActivityState>;
}

export const mergeEvents = ({ currentEvents, nextEvents }: MergeEventsParams): Array<ActivityState> =>
  produce(currentEvents, (draft: Array<ActivityState>) => {
    // Create a Set of existing event IDs for fast lookup
    const existingEventIds = new Set(draft.map((event) => event.id));

    // Only add events that don't already exist
    const newUniqueEvents = nextEvents.filter((event) => !existingEventIds.has(event.id));

    draft.push(...newUniqueEvents);
  });

export const getEventTypes = (detectedObjects: Activity['objectsDetected']): Array<DetectionTypes> => {
  const detectedTypes = detectedObjects?.map((object) => object.type) || [];
  // If any AI_EVENTS is detected, return only the detected AI_EVENTS
  if (detectedTypes.some((object) => AI_EVENTS.includes(object))) {
    return Array.from(new Set(detectedTypes.filter((object) => AI_EVENTS.includes(object)))) as Array<DetectionTypes>;
  }
  // If any SOUND_EVENTS is detected, return 'sound'
  if (detectedTypes.some((object) => SOUND_EVENTS.includes(object))) {
    return ['sound'];
  }
  // If any MOTION_EVENTS is detected, return 'motion'
  if (detectedTypes.some((object) => MOTION_EVENTS.includes(object))) {
    return ['motion'];
  }
  // Otherwise, return an empty array
  return [];
};

type AppliedFilters = {
  isCameraActive: boolean;
  isDateTimeActive: boolean;
  isEventTypesActive: boolean;
};

export const getAppliedFilters = (currentState: LocalFilterState, initialState: LocalFilterState): AppliedFilters => {
  const isCameraActive = !!Object.values(currentState.leaves).filter(({ value }) => value === true).length;
  const isDateTimeActive =
    currentState.startTime !== initialState.startTime || currentState.endTime !== initialState.endTime;
  const isEventTypesActive = !!currentState.objectsDetected.length;

  return {
    isCameraActive,
    isDateTimeActive,
    isEventTypesActive,
  };
};

export const getNumberOfActiveFilters = (appliedFilters: AppliedFilters): number =>
  Object.values(appliedFilters).filter(Boolean).length;

interface AdjustTimeRangeParams {
  startTime: number | null;
  endTime: number | null;
  retentionDays: number;
}

export const adjustFilterRange = ({ startTime, endTime, retentionDays }: AdjustTimeRangeParams) => {
  const { earliestStartTime, latestEndTime } = getTimeRangeBasedOnRetention(retentionDays);

  const adjustedTimeRange = {
    startTime,
    endTime,
  };

  // Validate and adjust filter days

  if (adjustedTimeRange.startTime !== null) {
    const startTimeValidationResult = validateFilterDate({
      date: adjustedTimeRange.startTime,
      retentionDays,
    });
    if (!startTimeValidationResult.isValid) {
      adjustedTimeRange.startTime = earliestStartTime.valueOf();
    }
  }

  if (adjustedTimeRange.endTime !== null) {
    const endTimeValidationResult = validateFilterDate({
      date: adjustedTimeRange.endTime,
      retentionDays,
    });
    if (!endTimeValidationResult.isValid) {
      adjustedTimeRange.endTime = latestEndTime.valueOf();
    }
  }

  // Validate filter date range
  const dateRangeValidationResult = validateFilterDateRange({
    startDate: adjustedTimeRange.startTime || earliestStartTime.valueOf(),
    endDate: adjustedTimeRange.endTime || latestEndTime.valueOf(),
  });
  if (!dateRangeValidationResult.isValid) {
    adjustedTimeRange.startTime = earliestStartTime.valueOf();
    adjustedTimeRange.endTime = latestEndTime.valueOf();
  }

  return {
    startTime: adjustedTimeRange.startTime,
    endTime: adjustedTimeRange.endTime,
  };
};

interface ConvertFilterStateToQueryStatesParams {
  filterState: LocalFilterState;
  retentionDays: number;
}

export const convertFilterStateToQueryStatesPayload = ({
  filterState,
  retentionDays,
}: ConvertFilterStateToQueryStatesParams): QueryStatesPayload => {
  const leavesFilter = Object.values(filterState.leaves)
    .filter(({ value }) => value === true)
    .map(({ id, type }) => ({
      leafId: id,
      leafType: type,
    }));

  return {
    ...filterState,
    startTime: filterState.startTime || getTimeRangeBasedOnRetention(retentionDays).earliestStartTime.valueOf(),
    endTime: filterState.endTime || getTimeRangeBasedOnRetention(retentionDays).latestEndTime.valueOf(),
    leaves: leavesFilter,
  };
};
